/* eslint-disable max-params */
/* eslint-disable max-lines */
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { submitDtfEvent, submitCompletedEvent } from "common/utils/mixpanel/submit-dtf-events";
import { logUploadErrorEvent } from "common/utils/mixpanel/dropzone-error-event-tracker";
import { track } from "common/utils/mixpanel/tracker";
import {
  track as rudderstackTrack,
  trackRudderstackImageUploadStarted,
  trackRudderstackImageProcessingCompleted,
  trackRudderstackImageRemoved,
  trackRudderstackImageEdited
} from "common/utils/rudderstack/tracker";

export const BUSINESS_LINE = "Print";
export const PAGE_NAME = "print_order_builder";
const $getQuantityInput = () => document.querySelector(".js-quantity-form-input");

export const EVENTS = {
  PLACEMENT: {
    CREATE: "print_create_placement",
    UPDATE: "print_update_placement",
    DESTROY: "print_destroy_placement"
  },
  CHANGE: {
    QUANTITY_SLIDER: "print_quantity_slider_change",
    MEASUREMENTS_POSITION: "print_see_measurements_position_changed",
    ZIPCODE_LOCATION: "print_zipcode_location_changed",
    DELIVERY_DATE: "print_delivery_date_changed"
  },
  CLICK: {
    PRODUCT: "print_product_clicked",
    COMPOSITION: "print_composition_clicked",
    ITEM_EDIT: "print_item_edit_clicked",
    ITEM_REMOVE: "print_item_remove_clicked",
    CART_ICON: "print_cart_icon_clicked",
    REMOVE_ARTWORK: "print_remove_artwork_clicked",
    CANCEL_ARTWORK: "print_cancel_artwork_upload_clicked",
    PRINT_REVIEWS: "print_reviews_clicked",
    PRINT_EXPLORE_MORE: "print_explore_more_apparel_clicked"
  },
  MODAL: {
    MEASUREMENTS_MODAL_CLOSE: "print_see_measurements_closed",
    MEASUREMENTS_MODAL_OPEN: "print_see_measurements_clicked"
  },
  SCALED: {
    DESIGN: "print_design_scaled"
  }
};

// eslint-disable-next-line import/prefer-default-export
export const trackImageUploadStart = file => {
  const { catalogNumber } = $getQuantityInput()?.dataset || {};
  const uploadUuid = crypto.randomUUID();

  submitDtfEvent("image_upload_started", {
    page: PAGE_NAME,
    type: "browse", // Note: we support only browse for now
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    dpi: 300,
    project_id: null,
    multi_artwork: false,
    artworks_count: 1,
    business_line: BUSINESS_LINE,
    order_type: BUSINESS_LINE.toLowerCase(),
    catalog_number: catalogNumber,
    upload_uuid: uploadUuid
  });
  trackRudderstackImageUploadStarted({
    catalog_number: catalogNumber,
    location: PAGE_NAME,
    file_type: file.type,
    file_size: file.size,
    file_name: file.name,
    dpi: 300,
    business_line: BUSINESS_LINE,
    artworks_count: 1,
    upload_uuid: uploadUuid
  });

  return uploadUuid;
};

export const trackImageUploadCompleted = ({ artwork, file, uploadStartTime, uploadUuid }) => {
  submitCompletedEvent({
    uploadedOn: PAGE_NAME,
    businessLine: BUSINESS_LINE,
    multiArtwork: false,
    orderType: BUSINESS_LINE.toLowerCase(),
    dpi: 300,
    artworksCount: 1,
    uploadStartTime,
    file,
    artwork,
    uploadUuid
  });
};

export const trackUploadError = ({ file, errorResponse }) => {
  const pageInfo = {
    pageValue: PAGE_NAME,
    businessLine: BUSINESS_LINE,
    orderType: BUSINESS_LINE.toLowerCase()
  };
  logUploadErrorEvent(pageInfo, file, {
    response: {
      data: errorResponse
    }
  });
};

export const trackImageProcessingCompleted = ({ result, dtfChannelUuid, action }) => {
  submitDtfEvent("image_processing_completed", {
    payload: {
      process_data: result,
      stream_name: `dtf_channel_${dtfChannelUuid}`,
      action_type: action
    },
    artwork_id: result.artwork_id,
    business_line: BUSINESS_LINE,
    page: PAGE_NAME,
    order_type: BUSINESS_LINE.toLowerCase()
  });
  const {
    filetype,
    filesize,
    filename,
    original_dpi: dpi,
    metadata,
    no_bg_imgix_url: noBgImgixUrl,
    with_bg_imgix_url: withBgImgixUrl,
    original_image_imgix_url: originalImageImgixUrl
  } = result.process_data;

  const enhancedImageUrl = noBgImgixUrl || withBgImgixUrl;
  const originalImageUrl = originalImageImgixUrl;

  trackRudderstackImageProcessingCompleted({
    artwork_id: result.artwork_id,
    location: PAGE_NAME,
    catalog_number: metadata?.catalog_number,
    file_type: filetype,
    file_size: filesize,
    file_name: filename,
    dpi,
    business_line: BUSINESS_LINE,
    artworks_count: 1,
    ...(enhancedImageUrl && { enhanced_image_url: enhancedImageUrl }),
    ...(originalImageUrl && { original_image_url: originalImageUrl })
  });
};

/* eslint-disable max-lines-per-function */
export const trackPlacementEvent = (eventName, placement, additionalData = {}) => {
  const { id, dtf_artwork: dtfArtwork, position, width, height } = placement;
  const { removeBackground, withBgUrl, noBgUrl, id: artworkId } = objNamesToCamelCase(dtfArtwork);

  const commonData = {
    artwork_id: artworkId,
    placement_id: id,
    background_removed: removeBackground,
    artwork_url: removeBackground ? noBgUrl : withBgUrl
  };

  let eventData;

  if (eventName === EVENTS.PLACEMENT.UPDATE) {
    const { oldHeight, oldWidth, oldPosition, action } = additionalData;
    eventData = {
      ...commonData,
      action,
      old_position: oldPosition,
      new_position: position,
      old_dimensions: { width: oldWidth, height: oldHeight },
      new_dimensions: { width, height }
    };

    const currentStoreCode = document.documentElement.lang === "en-CA" ? "ca" : "us";

    rudderstackTrack("Print Placement Updated", {
      print_location: position,
      store_code: currentStoreCode,
      is_app: Boolean(window.MOBILE_APP),
      location: PAGE_NAME,
      platform: window.MOBILE_APP_PLATFORM || "web",
      url: window.location.href
    });
  } else if (eventName === EVENTS.PLACEMENT.DESTROY) {
    const { file, filetype, filesize, filename } = dtfArtwork;
    trackRudderstackImageRemoved({
      file_name: filename || file?.name,
      file_type: filetype || file?.type,
      file_size: filesize || file?.size,
      location: PAGE_NAME,
      business_line: BUSINESS_LINE,
      artworks_count: 1
    });
  } else {
    eventData = {
      ...commonData,
      position,
      width,
      height
    };
  }

  track(eventName, eventData);
};

export const trackProductClick = (product, composition, sectionContext, productPosition) => {
  track(EVENTS.CLICK.PRODUCT, {
    version: "2",
    product: product.name,
    product_id: product.id,
    quality: product.quality,
    click_context: `${sectionContext}_${product.name}`,
    brand: product.brand,
    product_category: product.category,
    spree_composition_id: composition.id,
    composition: composition.colorName,
    page_name: PAGE_NAME,
    item_position: parseInt(productPosition, 10) + 1, // start with 1
    business_line: BUSINESS_LINE
  });
};

export const trackCompositionClick = (composition, product, compositionsInCart) => {
  track(EVENTS.CLICK.COMPOSITION, {
    product_id: product.id,
    spree_composition_id: composition.id,
    in_cart: composition.id in compositionsInCart,
    composition_name: composition.colorName
  });
};

export const trackItemClick = ({ action, product, composition, group, sizes }) => {
  const eventName = action === "edit" ? EVENTS.CLICK.ITEM_EDIT : EVENTS.CLICK.ITEM_REMOVE;

  track(eventName, {
    product_id: product.id,
    blank_name: product.name,
    composition_id: composition.id,
    composition_name: composition.colorName,
    sizes,
    variant_ids: Object.keys(group.variants),
    blank_brand: product.brand,
    blank_catalog_number: product.catalogNumber
  });
};

export const trackCartIconClick = () => {
  track(EVENTS.CLICK.CART_ICON);
};

export const trackRemoveArtworkClick = () => {
  track(EVENTS.CLICK.REMOVE_ARTWORK);
};

export const trackCancelArtworkUploadClick = artworkId => {
  track(EVENTS.CLICK.CANCEL_ARTWORK, { artwork_id: artworkId });
};

export const trackZipcodeLocationChange = (oldZipcode, newZipcode) => {
  track(EVENTS.CHANGE.ZIPCODE_LOCATION, { old_zipcode: oldZipcode, new_zipcode: newZipcode });
};

export const trackPrintReviewsClick = product => {
  track(EVENTS.CLICK.PRINT_REVIEWS, {
    product_id: product.id,
    brand: product.brand,
    catalog_number: product.catalogNumber,
    avg_rating: product.averageRating,
    total_count: product.reviewsCount
  });
};

export const trackPrintDesignScaled = eventData => {
  track(EVENTS.SCALED.DESIGN, eventData);

  const { artwork, price, editType } = eventData;
  if (!artwork) return;

  trackRudderstackImageEdited({
    artwork,
    edit_type: editType,
    location: PAGE_NAME,
    business_line: BUSINESS_LINE,
    artworks_count: 1,
    old_price: price,
    new_price: price
  });
};

export const trackBackgroundToggle = (artwork, price, toggleValue) => {
  trackRudderstackImageEdited({
    artwork,
    edit_type: toggleValue ? "remove background" : "keep background",
    location: PAGE_NAME,
    business_line: BUSINESS_LINE,
    artworks_count: 1,
    old_price: price,
    new_price: price
  });
};

export const trackDeliveryDateChange = (oldDate, newDate) => {
  if (!oldDate || oldDate === newDate) return;

  track(EVENTS.CHANGE.DELIVERY_DATE, { old_date: oldDate, new_date: newDate });
};

export const trackExploreMoreApparelClick = () => {
  track(EVENTS.CLICK.PRINT_EXPLORE_MORE);
};

export const trackMeasurementsModalClose = () => {
  track(EVENTS.MODAL.MEASUREMENTS_MODAL_CLOSE);
};

export const trackMeasurementsModalOpen = () => {
  track(EVENTS.MODAL.MEASUREMENTS_MODAL_OPEN);
};

export const trackMeasurementsPositionChange = (newPosition, oldPosition) => {
  track(EVENTS.CHANGE.MEASUREMENTS_POSITION, {
    old_position: oldPosition,
    new_position: newPosition
  });
};
